import React from 'react';
import { getTranslation } from './languages';

/**
 * A modal that prompts the user to rate a concept before proceeding.
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {function} props.onClose - Function to call when closing the modal.
 * @param {function} props.onStatusSelect - Function to call when a status button is clicked.
 * @param {string} props.conceptTitle - The title of the concept to be rated.
 * @param {string} props.uiLanguage - The UI language code.
 */
export default function RateConceptModal({ isOpen, onClose, onStatusSelect, conceptTitle, uiLanguage }) {
    const t = (key, params = {}) => getTranslation(key, uiLanguage, params);
    if (!isOpen) return null;

    const handleButtonClick = (status) => {
        if (status) {
            onStatusSelect(status);
        }
        onClose(); // Close the modal after selection or cancel
    };

    const handleBackdropClick = (e) => {
        // Only close if clicking on the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
            onMouseDown={handleBackdropClick}
        >
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in-up" onMouseDown={e => e.stopPropagation()}>
                <h2 className="text-xl font-bold mb-2 text-gray-800">{t('pleaseRateThisConcept')}</h2>
                <p className="text-gray-600 mb-6">{t('beforeStartingDeepDive', { conceptTitle })}</p>
                
                <h4 className="text-md font-semibold text-gray-700 mb-3">{t('howDoesConceptFeel')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <button onClick={() => handleButtonClick('easy')} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-sky-100 text-sky-800 hover:bg-sky-200 hover:scale-105">👌 {t('tooEasy')}</button>
                    <button onClick={() => handleButtonClick('known')} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-green-100 text-green-800 hover:bg-green-200 hover:scale-105">✅ {t('alreadyKnown')}</button>
                    <button onClick={() => handleButtonClick('partial')} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 hover:scale-105">🤔 {t('partiallyKnow')}</button>
                    <button onClick={() => handleButtonClick('new')} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-rose-100 text-rose-800 hover:bg-rose-200 hover:scale-105">💡 {t('newToMe')}</button>
                    <button onClick={() => handleButtonClick('hard')} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-purple-100 text-purple-800 hover:bg-purple-200 hover:scale-105">🤯 {t('tooHard')}</button>
                    <button onClick={() => handleButtonClick(null)} className="p-3 rounded-md text-left text-sm font-medium transition-all duration-150 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105">{t('cancel')}</button>
                </div>
            </div>
        </div>
    );
}
